import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageCircle, TrendingUp, TrendingDown, Users, ChevronRight } from 'lucide-react';

import { fetchPostsForStock } from '../../../redux/userGroups/groupsToStock/groupsToStockActions';
import {
  selectPostsForTicker,
  selectPostsForTickerStatus,
} from '../../../redux/userGroups/groupsToStock/groupsToStocksSlice';
import CredibilityChip from '../../../components/Credibility/CredibilityChip';
import { resolveMediaUrl } from '../../../utils/resolveMediaUrl';

const formatRelative = (dateString, t) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  const diffH = Math.floor((Date.now() - date.getTime()) / 3600000);
  if (diffH < 1) return t('stocks.time.justNow');
  if (diffH < 24) return t('stocks.time.hoursAgo', { count: diffH });
  if (diffH < 48) return t('stocks.time.yesterday');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const PostCard = ({ post, onOpenGroup, t }) => {
  const initial = (post.username || '?').charAt(0).toUpperCase();

  return (
    <button
      onClick={() => onOpenGroup(post.group_id)}
      className="w-full text-left p-4 rounded-xl border border-gray-200 bg-gray-50 hover:bg-gray-100 hover:border-indigo-400/50 dark:border-slate-600 dark:bg-slate-800/60 dark:hover:bg-slate-800 dark:hover:border-indigo-500/50 transition-all duration-300 group"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          {post.profile_picture ? (
            <img src={resolveMediaUrl(post.profile_picture)} alt={post.username} className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
          ) : (
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-semibold text-white">{initial}</span>
            </div>
          )}
          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{post.username}</span>
          <CredibilityChip cred={post.author_credibility} size="xs" showAccuracy={false} />
          <span className="text-gray-400 dark:text-gray-600">&middot;</span>
          <span className="text-xs text-gray-500 flex-shrink-0">{formatRelative(post.created_at, t)}</span>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-indigo-600 dark:text-gray-500 dark:group-hover:text-indigo-400 flex-shrink-0 transition-colors" />
      </div>

      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1 line-clamp-1">{post.title}</h4>
      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">{post.content}</p>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <TrendingUp className="w-3 h-3 text-emerald-700 dark:text-emerald-400" />
            {post.bull_count || 0}
          </span>
          <span className="flex items-center gap-1">
            <TrendingDown className="w-3 h-3 text-rose-600 dark:text-rose-400" />
            {post.bear_count || 0}
          </span>
        </div>
        <span className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 dark:text-indigo-300 bg-indigo-100 dark:bg-indigo-500/10 border border-indigo-500/20 rounded-full px-2.5 py-1">
          <Users className="w-3 h-3" />
          {post.group_name}
        </span>
      </div>
    </button>
  );
};

const CommunityGroupPosts = ({ symbol }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const posts = useSelector((s) => selectPostsForTicker(s, symbol));
  const status = useSelector((s) => selectPostsForTickerStatus(s, symbol));

  useEffect(() => {
    if (symbol) {
      dispatch(fetchPostsForStock({ ticker: symbol, limit: 10 }));
    }
  }, [dispatch, symbol]);

  const handleOpenGroup = (groupId) => navigate(`/groups/${groupId}`);

  return (
    <div className="h-full flex flex-col bg-gray-50/80 dark:bg-slate-900/50 backdrop-blur-xl rounded-2xl border border-gray-200 dark:border-white/10 overflow-hidden shadow-2xl shadow-black/5 dark:shadow-black/20">
      <div className="p-6 border-b border-gray-200 dark:border-white/10 bg-gradient-to-r from-gray-100/60 via-gray-100/40 to-transparent dark:from-white/5 dark:via-white/10 dark:to-transparent backdrop-blur-sm">
        <h2 className="text-2xl font-bold bg-gradient-to-r from-violet-600 via-indigo-600 to-blue-600 dark:from-violet-400 dark:via-indigo-300 dark:to-blue-400 bg-clip-text text-transparent mb-1">
          {t('stocks.community.heading')}
        </h2>
        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm">
          <MessageCircle className="w-4 h-4" />
          <span>{t('stocks.community.subtitle', { symbol })}</span>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-6">
        {status === 'loading' && posts.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 rounded-full border-2 border-indigo-500/30 border-t-indigo-500 animate-spin" />
          </div>
        )}

        {status !== 'loading' && posts.length === 0 && (
          <div className="text-center py-12 px-4">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-indigo-100 dark:bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
              <MessageCircle className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
            </div>
            <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{t('stocks.community.emptyTitle', { symbol })}</p>
            <p className="text-xs text-gray-500 mt-1">
              {t('stocks.community.emptySubtitle')}
            </p>
          </div>
        )}

        {posts.length > 0 && (
          <div className="space-y-3">
            {posts.map((post) => (
              <PostCard key={post.id} post={post} onOpenGroup={handleOpenGroup} t={t} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityGroupPosts;
